import { computed, shallowRef, watch } from 'vue';
import { defineStore, acceptHMRUpdate, storeToRefs } from 'pinia';

import { useMaterialSwatchesStore } from '@/stores/materialSwatches';
import type {
  PartsCreateData,
  PartsFromContoursData,
} from '@cutr/constants/part';
import type {
  PartNode,
  PartDoNotProduceData,
  PartDisplayNameData,
  PartUpdateData,
} from '@/api/parts';
import { useOrderFiles } from '@/stores/orderFiles';
import type { HardwareType } from '@cutr/constants/hardware';
import { computeRequiredHardwareQuantity } from '@cutr/constants/hardware';
import { useOrderStore } from '@/stores/order';
import {
  usePartsQuery,
  usePartsMutations,
  useInvalidateParts,
} from '@/queries/parts';

export type NodeForHash = { id?: number; nodeId: number; fileId: string };

export type MaterialUpdateData = Omit<PartUpdateData, 'properties'>;
type PropertiesUpdateData = Required<
  Pick<PartUpdateData, 'partIds' | 'properties'>
>;

type IdInFile = number | undefined;
type ByFile<T> = ReadonlyMap<string, ReadonlyMap<IdInFile, T>>;

// A zero `nodeId` falls back to `id` (CTR-15660).
function idInFile(n: NodeForHash): IdInFile {
  return n.nodeId || n.id;
}

/** The identity of a node across files, for callers that need a flat key. */
export function nodeKey(n: NodeForHash) {
  return `${n.fileId}:${idInFile(n)}`;
}

// Nested rather than keyed by `nodeKey`: no string is built per lookup, and a
// toggle only copies the files it touches.
function groupByFile<T extends NodeForHash>(nodes: readonly T[]) {
  const byFile = new Map<string, Map<IdInFile, T>>();
  for (const n of nodes) {
    let inFile = byFile.get(n.fileId);
    if (!inFile) byFile.set(n.fileId, (inFile = new Map()));
    inFile.set(idInFile(n), n);
  }
  return byFile;
}

function sameNodes(a: ByFile<unknown>, b: ByFile<unknown>) {
  if (a.size !== b.size) return false;
  for (const [fileId, inFile] of a) {
    const other = b.get(fileId);
    if (!other || other.size !== inFile.size) return false;
    for (const id of inFile.keys()) if (!other.has(id)) return false;
  }
  return true;
}

export const usePartsStore = defineStore('parts', () => {
  const { activeVariantId } = storeToRefs(useOrderStore());
  const { nodes, materialGroups, latestUpdatedAt } = usePartsQuery(
    () => activeVariantId.value
  );
  const mutations = usePartsMutations();
  const invalidatePartsForVariant = useInvalidateParts();

  // Replaced whole on every write, so readers never need a deep watch.
  const selection = shallowRef<ByFile<NodeForHash>>(new Map());

  const fileNodesIndex = computed(() => groupByFile(nodes.value));

  // Keyed by file id. A plain object rather than a `Map` so the shape callers
  // already read stays put; a fresh object per build is what notifies them.
  const fileNodesMap = computed(() =>
    nodes.value.reduce(
      (byFile, node) => {
        (byFile[node.fileId] ||= []).push(node);
        return byFile;
      },
      {} as Record<string, PartNode[]>
    )
  );
  const materialSwatchesStore = useMaterialSwatchesStore();

  const materialGroupsById = computed(
    () => new Map(materialGroups.value.map((group) => [group.id, group]))
  );
  const materialSwatchesById = computed(
    () =>
      new Map(
        materialSwatchesStore.materialSwatches
          .filter((swatch) => swatch.id)
          .map((swatch) => [swatch.id, swatch])
      )
  );

  /** The relation the payload leaves out — see `PartNode`. */
  function materialGroupFor(node: Pick<PartNode, 'supplierMaterialGroupId'>) {
    return materialGroupsById.value.get(node.supplierMaterialGroupId);
  }

  function swatchFor(node: Pick<PartNode, 'materialSwatchId'>) {
    if (!node.materialSwatchId) return undefined;
    return materialSwatchesById.value.get(node.materialSwatchId);
  }

  const orderFiles = useOrderFiles();

  // The viewer echoes every selection it is given back as a change event, so a
  // write that changes nothing must not notify anyone.
  function writeSelection(next: ByFile<NodeForHash>) {
    if (sameNodes(next, selection.value)) return;
    selection.value = next;
  }

  function reset() {
    selection.value = new Map();
  }

  function setSelection(nodes: NodeForHash[]) {
    writeSelection(groupByFile(nodes));
  }

  function clearSelection() {
    writeSelection(new Map());
  }

  function isSelected(n: NodeForHash) {
    return selection.value.get(n.fileId)?.has(idInFile(n)) ?? false;
  }

  /** Whether the selection is these nodes and nothing else. */
  function isSelectionExactly(nodes: NodeForHash[]) {
    return sameNodes(groupByFile(nodes), selection.value);
  }

  function withVariant<T, R>(
    call: (orderVariantId: string, data: T) => Promise<R>
  ) {
    return (data: T) => {
      const orderVariantId = activeVariantId.value;
      if (!orderVariantId) return Promise.resolve();

      return call(orderVariantId, data);
    };
  }

  function invalidateParts() {
    return invalidatePartsForVariant(activeVariantId.value);
  }

  const createParts = withVariant((orderVariantId, data: PartsCreateData) =>
    mutations.createParts({ orderVariantId, data })
  );

  const createPartsFromContours = withVariant(
    (orderVariantId, data: PartsFromContoursData) =>
      mutations.createPartsFromContours({ orderVariantId, data })
  );

  const updatePartMaterials = withVariant(
    (orderVariantId, data: MaterialUpdateData) =>
      mutations.updatePartMaterials({ orderVariantId, data })
  );

  const updatePartProperties = withVariant(
    (orderVariantId, data: PropertiesUpdateData) =>
      mutations.updatePartProperties({ orderVariantId, data })
  );

  const updatePartDisplayName = withVariant(
    (orderVariantId, data: PartDisplayNameData) =>
      mutations.updatePartDisplayName({ orderVariantId, data })
  );

  const updateDoNotProduce = withVariant(
    (orderVariantId, data: PartDoNotProduceData) =>
      mutations.updateDoNotProduce({ orderVariantId, data })
  );

  // Node ids are only meaningful within a variant, and the prune below cannot
  // stand in for this: it is skipped when the new variant has no quoting files,
  // and it reads whichever files are loaded when the parts land.
  watch(activeVariantId, clearSelection);

  // With no quoting files there is nothing to prune against, and pruning would
  // drop the whole selection.
  watch(nodes, () => {
    const fileIds = orderFiles.filesForQuoting.map((f) => f.id);
    if (!fileIds.length) return;

    writeSelection(
      new Map(
        [...selection.value].filter(([fileId]) => fileIds.includes(fileId))
      )
    );
  });

  function countSelected(nodes: NodeForHash[]) {
    let count = 0;
    for (const n of nodes) if (isSelected(n)) count++;
    return count;
  }

  // Toggle selection of supplied nodes. If all of them are selected already,
  // deselect them. Otherwise just add the ones that are not selected yet.
  function toggleSelection(nodes: NodeForHash[]) {
    const missing = nodes.filter((n) => !isSelected(n));
    const next = new Map(selection.value);
    const copied = new Map<string, Map<IdInFile, NodeForHash>>();
    const fileSelection = (fileId: string) => {
      let inFile = copied.get(fileId);
      if (inFile) return inFile;
      inFile = new Map(selection.value.get(fileId));
      copied.set(fileId, inFile);
      next.set(fileId, inFile);
      return inFile;
    };

    if (missing.length) {
      for (const n of missing) fileSelection(n.fileId).set(idInFile(n), n);
    } else {
      for (const n of nodes) fileSelection(n.fileId).delete(idInFile(n));
    }
    for (const [fileId, inFile] of copied) {
      if (!inFile.size) next.delete(fileId);
    }

    writeSelection(next);
  }

  function getNode(n: NodeForHash) {
    return fileNodesIndex.value.get(n.fileId)?.get(idInFile(n));
  }

  // A `Set` rather than a `partId -> node` index: same linear cost, and it
  // keeps the results in node order the way callers get them today.
  function getNodesFromPartIds(partIds: PartNode['partId'][]) {
    const wanted = new Set(partIds);
    return nodes.value.filter((n) => wanted.has(n.partId || ''));
  }

  const currSelection = computed(() =>
    [...selection.value.values()].flatMap((inFile) => [...inFile.values()])
  );

  const selectedCount = computed(() => {
    let count = 0;
    for (const inFile of selection.value.values()) count += inFile.size;
    return count;
  });

  // Only the nodes the cache knows: the viewer can select ids that are not
  // parts. `selectedCount` counts those too.
  const selectedNodes = computed(() =>
    currSelection.value.flatMap((n) => getNode(n) ?? [])
  );

  const selectedPartIds = computed(() =>
    selectedNodes.value.flatMap((node) => node.partId || [])
  );

  const selectionForAnalytics = computed(() => ({
    partsCount: selectedCount.value,
    filesCount: selection.value.size,
  }));

  const selectionByFile = computed(() => {
    const selected: Record<string, Set<number>> = {};
    for (const [fileId, inFile] of selection.value) {
      selected[fileId] = new Set([...inFile.values()].map((n) => n.nodeId));
    }
    return selected;
  });

  const partsForFile = fileNodesMap;

  const hasManualParts = (fileId: string) => {
    return (
      partsForFile.value[fileId]?.some((node) => node.source === 'manual') ??
      false
    );
  };

  const requiredHardwareQuantityTotals = computed(() =>
    selectedNodes.value.reduce(
      (acc, node) => {
        for (const [hardwareType, quantity] of Object.entries(
          computeRequiredHardwareQuantity(node)
        )) {
          if (!acc[hardwareType as HardwareType])
            acc[hardwareType as HardwareType] = 0;

          acc[hardwareType as HardwareType] += quantity;
        }
        return acc;
      },
      {} as Record<HardwareType, number>
    )
  );

  return {
    nodes,
    latestUpdatedAt,
    reset,
    invalidateParts,
    currSelection,
    selectedCount,
    isSelected,
    isSelectionExactly,
    countSelected,
    toggleSelection,
    clearSelection,
    selectedNodes,
    createParts,
    createPartsFromContours,
    updatePartMaterials,
    updatePartProperties,
    getNode,
    getNodesFromPartIds,
    setSelection,
    selectedPartIds,
    partsForFile,
    hasManualParts,
    selectionForAnalytics,
    selectionByFile,
    materialGroupsById,
    materialSwatchesById,
    materialGroupFor,
    swatchFor,
    getMaterials: () => materialGroups.value,
    updatePartDisplayName,
    updateDoNotProduce,
    requiredHardwareQuantityTotals,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePartsStore, import.meta.hot));
}
